import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import ProductCard from '../components/sections/ProductCard'
import FilterPanel, { formatINR, productTypeLabel } from '../components/products/FilterPanel'
import Select from "react-select"

const PER_PAGE = 12
const SEARCH_DEBOUNCE_MS = 400

// /product/search returns a slimmer resource than /products — normalise the
// fields ProductCard reads (slug, stock, category objects). Pricing comes
// pre-computed: `price` is the list price, `total_price` is after discount + GST.
const slugify = (name = '') =>
  name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

const normalizeSearchProduct = (p) => ({
  ...p,
  slug: p.slug ?? slugify(p.name),
  total_price: p.total_price ?? p.price,
  product_price: p.product_price ?? p.price,
  discount_rate: p.discount_rate ?? '0',
  discount_amount: p.discount_amount ?? '0',
  stock: p.stock ?? (p.in_stock ? 1 : 0),
  categories: (p.categories ?? []).map(c =>
    typeof c === 'string' ? { name: c, parent_id: null } : c
  ),
})

// Values are sent verbatim as the API `sort` param; '' omits it (server default:
// relevance when searching, catalogue order otherwise).
const SORTS = [
  { value: '', label: 'DEFAULT' },
  { value: 'price_low_high', label: 'INVESTMENT: LOW TO HIGH' },
  { value: 'price_high_low', label: 'INVESTMENT: HIGH TO LOW' },
  { value: 'newest', label: 'NEWEST FIRST' },
  { value: 'oldest', label: 'OLDEST FIRST' },
  { value: 'popularity', label: 'BEST SELLING' },
  { value: 'rating_desc', label: 'TOP RATED' },
  { value: 'discount_desc', label: 'BIGGEST DISCOUNT' },
  { value: 'name_asc', label: 'NAME: A TO Z' },
  { value: 'name_desc', label: 'NAME: Z TO A' },
  { value: 'ratti_desc', label: 'RATTI: HIGH TO LOW' },
  { value: 'ratti_asc', label: 'RATTI: LOW TO HIGH' },
  { value: 'carat_desc', label: 'CARAT: HIGH TO LOW' },
  { value: 'carat_asc', label: 'CARAT: LOW TO HIGH' },
]

// Advanced filters live in the query string (alongside ?s=) so every filtered
// view is shareable and survives reloads/back. Lists are comma-separated ids.
const csvIds = (v) => (v ? v.split(',').map(Number).filter(n => Number.isInteger(n) && n > 0) : [])
const csv = (v) => (v ? v.split(',').filter(Boolean) : [])
const num = (v) => (v === null || v === '' || !Number.isFinite(Number(v)) ? undefined : Number(v))

function readFilters(sp) {
  return {
    sort: sp.get('sort') ?? '',
    sub: csvIds(sp.get('sub')),
    minPrice: num(sp.get('min_price')),
    maxPrice: num(sp.get('max_price')),
    minRatti: num(sp.get('min_ratti')),
    maxRatti: num(sp.get('max_ratti')),
    minCarat: num(sp.get('min_carat')),
    maxCarat: num(sp.get('max_carat')),
    grades: csvIds(sp.get('grade')),
    origins: csvIds(sp.get('origin')),
    types: csv(sp.get('type')),
    tags: csvIds(sp.get('tag')),
    attrs: csvIds(sp.get('attr')),
    inStock: sp.get('in_stock') === '1',
    onSale: sp.get('on_sale') === '1',
    featured: sp.get('featured') === '1',
    special: sp.get('special') === '1',
    discount: num(sp.get('discount')),
    rating: num(sp.get('rating')),
  }
}

// Writes a patch of URL params onto a copy: arrays are comma-joined; null,
// undefined, '' and [] delete the key.
function applyPatch(prev, patch) {
  const next = new URLSearchParams(prev)
  for (const [key, value] of Object.entries(patch)) {
    const v = Array.isArray(value) ? value.join(',') : value
    if (v === null || v === undefined || v === '') next.delete(key)
    else next.set(key, String(v))
  }
  return next
}

// Every URL key the filter panel owns — cleared together by "clear all".
const FILTER_KEYS = [
  'sort', 'sub', 'min_price', 'max_price', 'min_ratti', 'max_ratti', 'min_carat', 'max_carat',
  'grade', 'origin', 'type', 'tag', 'attr', 'in_stock', 'on_sale', 'featured', 'special', 'discount', 'rating',
]

// The sort control sits inside the filter-console pill (icon + h-10 bordered
// box), so react-select renders borderless/transparent and only contributes
// the menu — colours come from the shop tokens (primary text, secondary hover).
const SORT_SELECT_STYLES = {
  container: (base) => ({ ...base, minWidth: '11.5rem' }),
  control: (base) => ({
    ...base,
    minHeight: 0,
    border: 'none',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    cursor: 'pointer',
  }),
  valueContainer: (base) => ({ ...base, padding: 0 }),
  singleValue: (base) => ({
    ...base,
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'color-mix(in srgb, var(--color-primary) 60%, transparent)',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 0 0 0.25rem',
    color: 'color-mix(in srgb, var(--color-primary) 30%, transparent)',
    '&:hover': { color: 'var(--color-secondary)' },
  }),
  menu: (base) => ({
    ...base,
    marginTop: '0.5rem',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    border: '1px solid var(--color-border)',
    boxShadow: '0 12px 32px -12px rgba(90, 10, 22, 0.25)',
  }),
  // The menu portals to <body> so the console card can't clip it — keep it
  // above the sticky header.
  menuPortal: (base) => ({ ...base, zIndex: 60 }),
  menuList: (base) => ({ ...base, padding: '0.25rem' }),
  option: (base, state) => ({
    ...base,
    borderRadius: '0.5rem',
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    color: state.isSelected ? 'white' : 'var(--color-primary)',
    backgroundColor: state.isSelected
      ? 'var(--color-secondary)'
      : state.isFocused
        ? 'color-mix(in srgb, var(--color-secondary) 10%, white)'
        : 'white',
    ':active': { backgroundColor: 'color-mix(in srgb, var(--color-secondary) 20%, white)' },
  }),
}

function SkeletonCard() {
  return (
    <div className="bg-white border border-gray-100 rounded-3xl overflow-hidden animate-pulse flex flex-col h-full">
      <div className="aspect-square bg-gray-200" />
      <div className="p-6 space-y-4 flex-1">
        <div className="flex justify-between">
          <div className="flex gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-3 w-3 rounded-full bg-gray-200" />
            ))}
          </div>
          <div className="h-3 w-20 rounded bg-gray-100" />
        </div>
        <div className="h-5 w-40 rounded bg-gray-200" />
        <div className="h-3 w-full rounded bg-gray-100" />
        <div className="h-3 w-3/4 rounded bg-gray-100" />
        <div className="pt-4 border-t border-dashed border-gray-100 flex justify-between items-center mt-auto">
          <div className="space-y-1">
            <div className="h-2 w-16 rounded bg-gray-100" />
            <div className="h-6 w-24 rounded bg-gray-200" />
          </div>
          <div className="h-3 w-16 rounded bg-gray-100" />
        </div>
      </div>
    </div>
  )
}

export default function Products() {
  // Category slug from the /:category route (e.g. /gemstone); absent on /products.
  const { category: categorySlug } = useParams()
  const navigate = useNavigate()
  // Search string from the ?s= query param (e.g. /products?s=ruby).
  const [searchParams, setSearchParams] = useSearchParams()
  const urlSearch = searchParams.get('s') ?? ''

  const filters = useMemo(() => readFilters(searchParams), [searchParams])
  const { sort } = filters
  const [filtersOpen, setFiltersOpen] = useState(false)

  const [query, setQuery] = useState(urlSearch)
  // Active category by slug ('ALL' = no filter), resolved to its numeric id
  // at fetch time. Seeded from the route so deep links start filtered.
  const [category, setCategory] = useState(categorySlug ?? 'ALL')
  // How many pages of the current result set are on screen. Tagged with the
  // search they belong to, so a filter change falls back to page 1 within the
  // same render instead of through an effect.
  const [pagesShown, setPagesShown] = useState({ key: null, pages: 1 })

  // Debounced copy of the search box so we don't hit the API per keystroke.
  const [debouncedQuery, setDebouncedQuery] = useState(urlSearch)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [query])

  // ── categories ────────────────────────────────────────────────────────────
  const { data: catData } = useQuery({
    queryKey: ['shopCategories'],
    queryFn: () => api.catalog.categories(),
    staleTime: 10 * 60 * 1000,
  })

  const apiCategories = catData?.data ?? []
  // /categories returns the whole tree flat (parents + children). Deep links
  // from home-page sections land on subcategory slugs, so lookups must cover
  // every level — the tabs below still show only top-level parents.
  const categoryBySlug = useMemo(
    () => Object.fromEntries(apiCategories.map(c => [c.slug, c])),
    [apiCategories],
  )
  const categoryById = useMemo(
    () => Object.fromEntries(apiCategories.map(c => [c.id, c])),
    [apiCategories],
  )
  const categoryTabs = useMemo(() => [
    { slug: 'ALL', label: 'ALL COLLECTIONS', img: null },
    ...apiCategories
      .filter(c => c.parent_id === null)
      .map(c => ({
        slug: c.slug,
        label: c.name.toUpperCase(),
        img: c.image_url ?? null,
      })),
  ], [apiCategories])

  // ── URL → filter sync ─────────────────────────────────────────────────────
  // The /:category slug is the source of truth for the active category filter.
  // Any slug in the tree is valid (parent or subcategory); once the category
  // list arrives, unknown slugs go back to /products so the URL never lies
  // about the applied filter.
  useEffect(() => {
    if (!categorySlug) {
      setCategory('ALL')
      return
    }
    setCategory(categorySlug)
    if (apiCategories.length === 0) return
    if (!categoryBySlug[categorySlug]) navigate('/products', { replace: true })
  }, [categorySlug, categoryBySlug, apiCategories.length, navigate])

  // ?s= → search box (deep links, back/forward, external navigations).
  useEffect(() => {
    setQuery(urlSearch)
  }, [urlSearch])

  // ── current page fetch ────────────────────────────────────────────────────
  // Every filter is applied server-side by /product/search.
  const q = debouncedQuery.trim()

  // The URL slug resolves to its numeric id here (the search takes ids).
  const activeCat = category !== 'ALL' ? categoryBySlug[category] : null
  const resolvingCategory = category !== 'ALL' && !activeCat

  // Tab to highlight: the active category's top-level ancestor, so a
  // subcategory deep link lights up its parent collection tab.
  let activeRoot = activeCat
  while (activeRoot && activeRoot.parent_id != null && categoryById[activeRoot.parent_id]) {
    activeRoot = categoryById[activeRoot.parent_id]
  }
  const activeRootSlug = activeRoot?.slug ?? 'ALL'

  // Subcategory multi-select inside the active collection: ?sub= wins; a
  // subcategory deep link (/ruby-stone…) counts as that one sub being ticked.
  const selectedSubIds = filters.sub.length
    ? filters.sub
    : activeCat && activeCat.parent_id != null ? [activeCat.id] : []
  const categoryIds = selectedSubIds.length ? selectedSubIds : activeCat ? [activeCat.id] : []

  const searchArgs = useMemo(() => {
    const f = filters
    return {
      ...(q && { q }),
      ...(categoryIds.length && { category_id: categoryIds }),
      ...(f.sort && { sort: f.sort }),
      ...(f.minPrice !== undefined && { min_price: f.minPrice }),
      ...(f.maxPrice !== undefined && { max_price: f.maxPrice }),
      ...(f.minRatti !== undefined && { min_ratti: f.minRatti }),
      ...(f.maxRatti !== undefined && { max_ratti: f.maxRatti }),
      ...(f.minCarat !== undefined && { min_carat: f.minCarat }),
      ...(f.maxCarat !== undefined && { max_carat: f.maxCarat }),
      ...(f.grades.length && { product_grade_id: f.grades }),
      ...(f.origins.length && { origin_id: f.origins }),
      ...(f.types.length && { product_type: f.types.join(',') }),
      ...(f.tags.length && { tag_id: f.tags }),
      ...(f.attrs.length && { attribute_value_id: f.attrs }),
      ...(f.inStock && { in_stock: 1 }),
      ...(f.onSale && { on_sale: 1 }),
      ...(f.featured && { is_featured: 1 }),
      ...(f.special && { is_special: 1 }),
      ...(f.discount && { min_discount: f.discount }),
      ...(f.rating && { min_rating: f.rating }),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, q, categoryIds.join(',')])
  const searchKey = JSON.stringify(searchArgs)

  // A filter/search change restarts at page 1 as soon as the key changes.
  const page = pagesShown.key === searchKey ? pagesShown.pages : 1

  // Each loaded page stays its own live query instead of being copied once
  // into a local array: when a product is edited in the admin, the next
  // refetch (mount, window focus, invalidation) re-renders every page already
  // on screen with the new price/stock rather than the first copy fetched.
  const pageQueries = useQueries({
    queries: Array.from({ length: page }, (_, i) => ({
      queryKey: ['shopProducts', searchKey, i + 1],
      queryFn: () => api.catalog.search({ ...searchArgs, page: i + 1, per_page: PER_PAGE }),
      // Deep links wait for the slug→id resolution, so the first fetch is
      // already filtered and never caches unfiltered rows under a filtered key.
      enabled: !resolvingCategory,
      // Catalogue edits must show up on the next visit: cached rows still
      // render instantly, but they are always revalidated behind them.
      staleTime: 0,
    })),
  })
  const pageData = pageQueries[page - 1]?.data
  const isFetching = pageQueries.some(r => r.isFetching)

  // Sidebar counts. Kept on screen while the next set loads so the panel
  // doesn't collapse to skeletons on every click.
  const { data: facetData } = useQuery({
    queryKey: ['shopFacets', searchKey],
    queryFn: () => api.catalog.search({ ...searchArgs, per_page: 1, include_facets: 1 }),
    enabled: !resolvingCategory,
    // Same reason as the product pages: the price slider and counts have to
    // follow admin edits too.
    staleTime: 0,
    placeholderData: keepPreviousData,
  })
  const facets = facetData?.facets

  const subOptions = useMemo(
    () => (activeRoot && facets ? facets.categories.filter(c => c.parent_id === activeRoot.id) : []),
    [activeRoot, facets],
  )

  // Loaded pages flattened in order and de-duplicated. Derived from the
  // queries on every render — never copied into state — so the grid always
  // shows what the API last returned.
  const loadedProducts = useMemo(() => {
    const seen = new Set()
    const rows = []
    for (const result of pageQueries) {
      for (const row of result.data?.data ?? []) {
        if (seen.has(row.id)) continue
        seen.add(row.id)
        rows.push(normalizeSearchProduct(row))
      }
    }
    return rows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageQueries.map(r => r.dataUpdatedAt).join(',')])

  const serverTotal = pageData?.meta?.total ?? 0
  const lastPage = pageData?.meta?.last_page ?? 1
  const canLoadMore = page < lastPage
  // Waiting on the category list to resolve a deep-linked slug shows the same
  // skeletons as the product fetch itself.
  const isFirstLoad = (isFetching || resolvingCategory) && loadedProducts.length === 0

  // Filtering + sorting happen server-side; loaded products render as-is.
  const filtered = loadedProducts

  const handleLoadMore = () => {
    if (!canLoadMore || isFetching) return
    setPagesShown({ key: searchKey, pages: page + 1 })
  }

  // Filter changes mirror into the URL (path = category, ?s= = search) with
  // `replace`, so they stay shareable without polluting browser history.
  const handleSearchChange = (val) => {
    setQuery(val)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (val.trim()) next.set('s', val)
      else next.delete('s')
      return next
    }, { replace: true })
  }
  // Switching collection keeps the search and other filters but drops the
  // subcategory picks, which belonged to the old collection.
  const handleCategoryChange = (slug) => {
    setCategory(slug)
    const params = applyPatch(searchParams, { sub: null }).toString()
    navigate(`${slug === 'ALL' ? '/products' : `/${slug}`}${params ? `?${params}` : ''}`, { replace: true })
  }
  const updateFilters = (patch) => setSearchParams(prev => applyPatch(prev, patch), { replace: true })
  // Ticking subcategories normalises the path to the collection root, with the
  // picks in ?sub= — so /ruby-stone… + "Emerald" becomes /gemstone?sub=52,49.
  const toggleSub = (id) => {
    if (!activeRoot) return
    const next = selectedSubIds.includes(id) ? selectedSubIds.filter(x => x !== id) : [...selectedSubIds, id]
    const params = applyPatch(searchParams, { sub: next }).toString()
    navigate(`/${activeRoot.slug}${params ? `?${params}` : ''}`, { replace: true })
  }
  const resetFilters = () => {
    setQuery('')
    setCategory('ALL')
    navigate('/products', { replace: true })
  }
  // Clears the panel's filters but keeps the search text and collection tab.
  const clearPanelFilters = () =>
    updateFilters(Object.fromEntries(FILTER_KEYS.filter(k => k !== 'sort').map(k => [k, null])))
  const handleSortChange = (val) => updateFilters({ sort: val || null })

  // Esc closes the mobile filter drawer.
  useEffect(() => {
    if (!filtersOpen) return
    const onKey = (e) => e.key === 'Escape' && setFiltersOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [filtersOpen])

  // ── active filter pills ───────────────────────────────────────────────────
  const nameOf = (list, id) => list?.find(x => x.id === id)?.name ?? `#${id}`
  const rangeLabel = (min, max, fmt) =>
    min !== undefined && max !== undefined ? `${fmt(min)} – ${fmt(max)}`
      : min !== undefined ? `${fmt(min)}+` : `UP TO ${fmt(max)}`
  const f = filters
  const pills = [
    ...selectedSubIds
      .filter(id => !(activeCat && activeCat.id === id && f.sub.length === 0))
      .map(id => ({ key: `sub-${id}`, label: (categoryById[id]?.name ?? `#${id}`).toUpperCase(), onRemove: () => toggleSub(id) })),
    ...(f.minPrice !== undefined || f.maxPrice !== undefined
      ? [{ key: 'price', label: rangeLabel(f.minPrice, f.maxPrice, formatINR), onRemove: () => updateFilters({ min_price: null, max_price: null }) }]
      : []),
    ...(f.minRatti !== undefined || f.maxRatti !== undefined
      ? [{ key: 'ratti', label: `RATTI ${rangeLabel(f.minRatti, f.maxRatti, v => v)}`, onRemove: () => updateFilters({ min_ratti: null, max_ratti: null }) }]
      : []),
    ...(f.minCarat !== undefined || f.maxCarat !== undefined
      ? [{ key: 'carat', label: `CARAT ${rangeLabel(f.minCarat, f.maxCarat, v => v)}`, onRemove: () => updateFilters({ min_carat: null, max_carat: null }) }]
      : []),
    ...f.grades.map(id => ({ key: `grade-${id}`, label: `GRADE ${nameOf(facets?.grades, id)}`, onRemove: () => updateFilters({ grade: f.grades.filter(x => x !== id) }) })),
    ...f.origins.map(id => ({ key: `origin-${id}`, label: nameOf(facets?.origins, id).toUpperCase(), onRemove: () => updateFilters({ origin: f.origins.filter(x => x !== id) }) })),
    ...f.types.map(t => ({ key: `type-${t}`, label: productTypeLabel(t).toUpperCase(), onRemove: () => updateFilters({ type: f.types.filter(x => x !== t) }) })),
    ...f.tags.map(id => ({ key: `tag-${id}`, label: `#${nameOf(facets?.tags, id)}`.toUpperCase(), onRemove: () => updateFilters({ tag: f.tags.filter(x => x !== id) }) })),
    ...f.attrs.map(id => {
      const value = facets?.attributes.flatMap(a => a.values).find(v => v.id === id)?.value ?? `#${id}`
      return { key: `attr-${id}`, label: String(value).toUpperCase(), onRemove: () => updateFilters({ attr: f.attrs.filter(x => x !== id) }) }
    }),
    ...(f.inStock ? [{ key: 'in_stock', label: 'IN STOCK', onRemove: () => updateFilters({ in_stock: null }) }] : []),
    ...(f.onSale ? [{ key: 'on_sale', label: 'ON SALE', onRemove: () => updateFilters({ on_sale: null }) }] : []),
    ...(f.featured ? [{ key: 'featured', label: 'FEATURED', onRemove: () => updateFilters({ featured: null }) }] : []),
    ...(f.special ? [{ key: 'special', label: 'SPECIAL', onRemove: () => updateFilters({ special: null }) }] : []),
    ...(f.discount ? [{ key: 'discount', label: `${f.discount}%+ OFF`, onRemove: () => updateFilters({ discount: null }) }] : []),
    ...(f.rating ? [{ key: 'rating', label: `${f.rating}★ & UP`, onRemove: () => updateFilters({ rating: null }) }] : []),
  ]
  const panelFilterCount = pills.length
  const hasAnyFilter = Boolean(query) || category !== 'ALL' || panelFilterCount > 0

  const panel = (
    <FilterPanel
      facets={facets}
      filters={filters}
      subOptions={subOptions}
      selectedSubIds={selectedSubIds}
      onToggleSub={toggleSub}
      onUpdate={updateFilters}
      onReset={clearPanelFilters}
      activeCount={panelFilterCount}
    />
  )

  return (
    <div className="flex flex-col min-h-screen bg-background font-normal uppercase select-none">

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#1E050A] pt-28 pb-16 normal-case select-none">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-linear-to-b from-[#2B050C] via-[#1A0307] to-[#0A0103]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(55%_60%_at_70%_30%,rgba(194,23,43,0.18),transparent_70%)] animate-pulse"></div>
          <div className="absolute inset-0 bg-[radial-gradient(45%_55%_at_15%_90%,rgba(122,13,30,0.28),transparent_70%)]"></div>
        </div>
        <div className="container relative z-10 mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.35em] text-[#E6C878] uppercase">
            <i className="fa-solid fa-star text-[9px] text-secondary"></i> ॥ COMPLETE COLLECTION ॥
          </span>
          <h1 className="mt-4 font-heading text-3xl md:text-5xl lg:text-6xl font-light uppercase leading-[1.1] text-[#FBF3E4]">
            ALL{' '}
            <span className="text-secondary drop-shadow-[0_2px_12px_rgba(242,160,7,0.35)] font-bold">
              DIVINE PRODUCTS
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-xs md:text-sm text-white/60 font-body leading-relaxed normal-case">
            Explore every lab-certified gemstone, energised Yantra, Rudraksha and sacred remedy —
            search, filter and sort to find the remedy aligned to your destiny.
          </p>
        </div>
      </section>

      {/* BREADCRUMB */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="container px-6 mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-bold tracking-[0.25em] text-primary/40">
            <Link className="hover:text-secondary transition-colors" to="/">HOME</Link>
            <i className="fa-solid fa-chevron-right text-[8px] text-secondary shrink-0 mx-1"></i>
            <Link className="hover:text-secondary transition-colors" to="/products">DIVINE SHOP</Link>
            <i className="fa-solid fa-chevron-right text-[8px] text-secondary shrink-0 mx-1"></i>
            <span className="text-secondary font-bold">
              {activeCat ? activeCat.name.toUpperCase() : 'ALL PRODUCTS'}
            </span>
          </nav>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-secondary animate-pulse"></div>
            <span className="text-[9px] font-mono font-bold text-primary/40 tracking-[0.2em]">
              {isFirstLoad ? '...' : `${serverTotal} SYSTEM ENTRIES`}
            </span>
          </div>
        </div>
      </div>

      {/* FILTER CONSOLE */}
      <section className="py-10 md:px-20">
        <div className="container px-6 mx-auto max-w-350">
          <div className="bg-white rounded-2xl border border-gray-100 px-5 py-4 mb-8 shadow-sm space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">

              {/* Search */}
              <div className="relative flex-1 max-w-lg">
                <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-primary/30 text-xs"></i>
                <input
                  id="products-search"
                  value={query}
                  onChange={e => handleSearchChange(e.target.value)}
                  placeholder="SEARCH SACRED INVENTORY..."
                  className="w-full h-10 bg-gray-50/50 border border-gray-100 rounded-xl pl-10 pr-9 focus:ring-2 focus:ring-secondary/20 focus:border-secondary/40 outline-none text-[10px] font-mono tracking-wider transition-all"
                  type="text"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => handleSearchChange('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/30 hover:text-secondary transition-colors"
                  >
                    <i className="fa-solid fa-xmark text-xs"></i>
                  </button>
                )}
              </div>

              {/* Filters (mobile) + sort + clear */}
              <div className="flex flex-wrap items-center gap-3 self-start lg:self-auto">
                <button
                  type="button"
                  onClick={() => setFiltersOpen(true)}
                  className="lg:hidden h-10 px-4 rounded-xl text-[9px] font-bold tracking-widest border border-gray-100 bg-gray-50/50 text-primary/70 hover:border-secondary/40 hover:text-secondary transition-all flex items-center gap-2"
                >
                  <i className="fa-solid fa-sliders"></i> FILTERS
                  {panelFilterCount > 0 && (
                    <span className="text-[8px] font-mono px-1.5 py-0.5 rounded-md bg-secondary text-white">{panelFilterCount}</span>
                  )}
                </button>
                <div className="flex items-center gap-2 pl-4 pr-2 h-10 bg-gray-50/50 border border-gray-100 rounded-xl group hover:border-secondary/40 focus-within:border-secondary/40 transition-colors">
                  <i className="fa-solid fa-arrow-down-short-wide text-primary/30 group-hover:text-secondary transition-colors text-sm"></i>
                  <Select
                    inputId="products-sort"
                    aria-label="Sort products"
                    styles={SORT_SELECT_STYLES}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    options={SORTS}
                    value={SORTS.find(s => s.value === sort) ?? SORTS[0]}
                    onChange={opt => handleSortChange(opt?.value ?? '')}
                    isSearchable={false}
                  />
                </div>
                {hasAnyFilter && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="h-10 px-4 rounded-xl text-[9px] font-bold tracking-widest border border-secondary/40 text-secondary hover:bg-secondary hover:text-white transition-all flex items-center gap-2"
                  >
                    <i className="fa-solid fa-filter-circle-xmark"></i> CLEAR
                  </button>
                )}
              </div>
            </div>

            {/* Category tabs */}
            <div className="pt-3 border-t border-dashed border-gray-100">
              <div className="flex items-center gap-3 overflow-x-auto pb-1 scrollbar-none">
                {isFirstLoad
                  ? Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-9 w-28 rounded-xl bg-gray-100 animate-pulse shrink-0" />
                  ))
                  : categoryTabs.map(cat => {
                    const active = cat.slug === activeRootSlug
                    return (
                      <button
                        key={cat.slug}
                        id={`cat-tab-${cat.slug}`}
                        type="button"
                        onClick={() => handleCategoryChange(cat.slug)}
                        className={`h-9 px-5 rounded-xl text-[9px] font-bold tracking-widest transition-all whitespace-nowrap border flex items-center gap-2 ${active
                          ? 'bg-primary text-white border-primary shadow-md shadow-primary/10'
                          : 'bg-white border-gray-200 text-primary/80 hover:border-secondary hover:text-secondary hover:bg-background/30'
                          }`}
                      >
                        {cat.img
                          ? <img alt={cat.label} className="w-5 h-5 rounded-full object-cover shrink-0" src={cat.img} />
                          : <div className="h-1 w-1 rounded-full bg-secondary animate-pulse"></div>
                        }
                        {cat.label}
                        {active && (
                          <span className="text-[8px] font-mono px-1.5 py-0.5 rounded-md ml-1 text-white/50 bg-black/10">
                            {isFetching ? '…' : serverTotal}
                          </span>
                        )}
                      </button>
                    )
                  })
                }
              </div>
            </div>
          </div>

          {/* Active filter pills */}
          {hasAnyFilter && !isFirstLoad && (
            <div className="mb-6 flex items-center gap-3 flex-wrap">
              <span className="text-[9px] font-mono font-bold tracking-widest text-primary/40 uppercase">Filters:</span>
              {category !== 'ALL' && (
                <span className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-primary/5 border border-primary/10 text-[9px] font-bold text-primary/70 tracking-widest">
                  {activeCat?.name.toUpperCase()}
                  <button type="button" onClick={() => handleCategoryChange('ALL')}>
                    <i className="fa-solid fa-xmark text-[8px]"></i>
                  </button>
                </span>
              )}
              {query && (
                <span className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-secondary/5 border border-secondary/10 text-[9px] font-bold text-secondary tracking-widest">
                  "{query}"
                  <button type="button" onClick={() => handleSearchChange('')}>
                    <i className="fa-solid fa-xmark text-[8px]"></i>
                  </button>
                </span>
              )}
              {pills.map(p => (
                <span
                  key={p.key}
                  className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-secondary/5 border border-secondary/15 text-[9px] font-bold text-primary/70 tracking-widest"
                >
                  {p.label}
                  <button type="button" aria-label={`Remove ${p.label}`} onClick={p.onRemove}>
                    <i className="fa-solid fa-xmark text-[8px]"></i>
                  </button>
                </span>
              ))}
              <span className="text-[9px] font-mono text-primary/30">{serverTotal} result{serverTotal !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </section>

      {/* PRODUCT GRID + FILTER SIDEBAR */}
      <section className="py-10 bg-white md:px-20">
        <div className="container px-6 mx-auto max-w-350 lg:flex lg:items-start lg:gap-8">

          <aside className="hidden lg:block w-72 shrink-0 sticky top-36 max-h-[calc(100vh-10rem)] overflow-y-auto scrollbar-theme rounded-2xl border border-gray-100 bg-white px-5 py-5 shadow-sm">
            {panel}
          </aside>

          <div className="flex-1 min-w-0">
          {isFirstLoad ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
              {Array.from({ length: PER_PAGE }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : filtered.length > 0 ? (
            <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8 transition-opacity ${isFetching && page === 1 ? 'opacity-60' : ''}`}>
              {filtered.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          ) : (
            <div className="text-center py-24 normal-case">
              <i className="fa-solid fa-magnifying-glass text-3xl text-primary/20"></i>
              <p className="mt-4 text-sm text-primary/50 font-body">
                No products match your current filters.
              </p>
              <div className="mt-6 flex gap-3 justify-center flex-wrap">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary/5 border border-primary/10 text-[10px] font-bold text-primary/60 tracking-widest hover:bg-primary hover:text-white transition-all"
                >
                  <i className="fa-solid fa-rotate-left"></i> RESET FILTERS
                </button>
              </div>
            </div>
          )}

          {/* LOAD MORE / END STATE */}
          {!isFirstLoad && loadedProducts.length > 0 && (
            <div className="mt-16 flex flex-col items-center justify-center gap-4">
              {canLoadMore ? (
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={isFetching}
                  className="inline-flex items-center justify-center gap-3 px-8 h-12 rounded-xl bg-primary text-white font-semibold text-[10px] tracking-[0.2em] uppercase transition-all duration-300 hover:bg-secondary shadow-lg shadow-primary/10 active:scale-95 border-none outline-none cursor-pointer disabled:opacity-70"
                >
                  {isFetching && <i className="fa-solid fa-spinner text-sm animate-spin"></i>}
                  {isFetching ? 'Loading...' : 'Load More Entries'}
                </button>
              ) : (
                <p className="text-[10px] font-mono tracking-[0.25em] text-secondary font-bold uppercase bg-secondary/5 px-6 py-2.5 rounded-full border border-secondary/10">
                  ✨ All {serverTotal} Products Loaded
                </p>
              )}
              <p className="text-[10px] font-mono tracking-[0.3em] text-primary/40 font-bold uppercase">
                Loaded <span>{loadedProducts.length}</span> of <span>{serverTotal}</span>
              </p>
            </div>
          )}
          </div>
        </div>
      </section>

      {/* MOBILE FILTER DRAWER */}
      {filtersOpen && (
        <div className="fixed inset-0 z-1000 lg:hidden" role="dialog" aria-modal="true" aria-label="Filters">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFiltersOpen(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm bg-white shadow-2xl flex flex-col">
            <div className="flex-1 overflow-y-auto scrollbar-theme px-5 py-5">{panel}</div>
            <div className="border-t border-gray-100 p-4 flex gap-3">
              <button
                type="button"
                onClick={() => setFiltersOpen(false)}
                className="flex-1 h-11 rounded-xl bg-primary text-white text-[10px] font-bold tracking-[0.2em] hover:bg-secondary transition-colors"
              >
                {isFetching ? 'UPDATING…' : `SHOW ${serverTotal} RESULT${serverTotal !== 1 ? 'S' : ''}`}
              </button>
              <button
                type="button"
                aria-label="Close filters"
                onClick={() => setFiltersOpen(false)}
                className="h-11 w-11 rounded-xl border border-gray-200 text-primary/60 hover:text-secondary hover:border-secondary transition-colors"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
